import { useState, useEffect } from 'react';
/*
 * Hook to know if an image in a slider should be preload or not.
 *
 * currentIndex: index of the image visible on the slider. For example if we are showing image. For example if we
 * are showing first image in the slider 1/15, the currentIndex is 0. If we are showing image 7/15 current index is 6.
 *
 * index: is the index of the image that we are evaluating to know if it should be preloaded or not.
 *
 * numberOfImages: total number of images
 *
 * preload: number of images to preload, default = 2, indicates how many images to the right of
 * the image visible should be preloaded.
 *
 * Thinking that navigation using left arrow is less usual, only is preloaded one image
 * to the left of the current image.
 *
 */
type ImageConfigProps = {
  currentIndex: number;
  index: number;
  numberOfImages: number;
  preload?: number;
};

export function useImagePreload(imageConfig: ImageConfigProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const { currentIndex, index, numberOfImages, preload = 2 } = imageConfig;

  function shouldPreloadNext() {
    return index > currentIndex && currentIndex + preload >= index;
  }

  function shouldPreloadPrevious() {
    if (currentIndex === 0) {
      return numberOfImages - 1 === index;
    }
    return index < currentIndex && currentIndex - 1 <= index;
  }

  function shouldPreload() {
    return currentIndex === index || shouldPreloadNext() || shouldPreloadPrevious();
  }

  useEffect(() => {
    if (!shouldRender) {
      setShouldRender(shouldPreload());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const hook: [boolean] = [shouldRender];
  return hook;
}
